/** Response shapes of the lane B3 API (/api/nivision/*) as the web uses them. Source of truth: apps/api/src/modules/nivision. */
import type { AgentRole } from '@nw/shared';

export type SourceStatus = 'pending' | 'connected' | 'silent' | 'auth_failing';

/** D-85: bearer token (recommended default) | none (URL only) | hmac (advanced). */
export type AuthMode = 'bearer' | 'none' | 'hmac';

export interface SourceView {
  id: string;
  siteId: string;
  webhookUrl: string;
  authMode: AuthMode;
  status: SourceStatus | string;
  lastEventAt: string | null;
  consecutiveAuthFailures: number;
  lastAuthFailureAt: string | null;
  hasSecret: boolean;
  secretSlots: { slot: number; set: boolean }[];
  secretUpdatedAt: string | null;
  eventTypes: string[];
  jsonataTransform: string;
  setupSteps: string[];
  stats: { events7d: number; processed7d: number; rejected24h: number; lastRejectedAt: string | null };
  createdAt: string;
}

export type Scope = 'REPORTS' | 'ALERTS' | 'CONFIG';

export interface KeyView {
  siteId: string;
  keyPrefix: string;
  scopesDetected: Scope[] | string[];
  hasAlertsScope: boolean;
  lastVerifiedAt: string | null;
  lastError: string | null;
  backfillStatus: 'none' | 'running' | 'done' | 'failed' | string;
  backfillDoneAt: string | null;
  backfillCount: number | null;
  lastReconcileAt: string | null;
  rulesSyncedAt: string | null;
  agentsSyncedAt: string | null;
  updatedAt: string;
}

export interface DetectResult {
  tenant: { uuid: string; slug: string } | null;
  site: { uuid: string; slug: string } | null;
  scopes: Scope[];
  featuresDisabled: Scope[];
  existingSite: { id: string; clientId: string; displayName: string } | null;
}

export type SaveKeyResult = KeyView & { detected: DetectResult; siteVerified: boolean; warnings: string[] };

export interface SyncRun {
  id: string;
  kind: 'backfill' | 'reconcile' | 'refresh' | string;
  status: 'running' | 'ok' | 'failed' | string;
  startedAt: string;
  finishedAt: string | null;
  listed: number | null;
  digested: number | null;
  error: string | null;
}

export interface AgentRow {
  id: string;
  agentUuid: string;
  name: string | null;
  nameOverride: string | null;
  displayName: string | null;
  missingName: boolean;
  lastSeenAt: string | null;
  calls7d: number;
  /** D-124: the canonical agentUuid this row is merged into (null = a canonical row) */
  mergeInto: string | null;
  /** the canonical row's Agent.id */
  mergedIntoId: string | null;
  /** rows merged into this one */
  aliasCount: number;
  /** own calls + the aliases' (canonical rows); = calls7d for an alias */
  calls7dTotal: number;
  /** admin override: true = a person, false = a line / placeholder, null = automatic */
  isPerson: boolean | null;
  /** the automatic guess from the name */
  personAuto: boolean;
  /** D-174: warmer (טיוב) / seller, null = none */
  role: AgentRole | null;
}

/** PATCH /api/nivision/sites/:siteId/agents/:agentId - only the keys present are written */
export type AgentPatchBody = {
  nameOverride?: string | null;
  isPerson?: boolean | null;
  role?: AgentRole | null;
};

/** GET /api/nivision/sites/:siteId/agents — the rows and the same-first-name groups that look like one person */
export interface AgentsResponse {
  items: AgentRow[];
  suggestions: { firstName: string; agentIds: string[] }[];
}

export interface RuleRow {
  id: string;
  ruleUuid: string;
  name: string;
  description: string | null;
  severity: string;
  isActive: boolean;
  fieldLabel: string | null;
  syncedAt: string;
}

export interface RulesResponse {
  hasAlertsScope: boolean;
  syncedAt: string | null;
  items: RuleRow[];
}

export type RawEventStatus = 'received' | 'processed' | 'ignored' | 'rejected' | 'error';
export const EVENT_FILTERS = ['processed', 'duplicate', 'ignored', 'rejected', 'backfill', 'error', 'received'] as const;
export type EventFilter = (typeof EVENT_FILTERS)[number];

export interface EventRow {
  id: string;
  eventId: string;
  type: string;
  status: RawEventStatus;
  origin: 'webhook' | 'backfill' | 'reconcile' | string;
  receivedAt: string;
  site: { id: string; displayName: string } | null;
  client: { id: string; name: string } | null;
  callUuid: string | null;
  callDigestId: string | null;
  signalCount: number | null;
  duplicateCount: number;
  lastDuplicateAt: string | null;
  reason: string | null;
  error: string | null;
  resultText: string;
  reprocessable: boolean;
}

export interface EventCheck {
  key: 'signature' | 'timestamp' | 'site' | 'duplicate' | 'ip';
  label: string;
  state: 'ok' | 'bad' | 'warn' | 'todo';
  text: string;
}

export interface EventDetail extends EventRow {
  headers: { webhookId: string | null; webhookTimestamp: string | null; signaturePreview: string | null; authMode: string | null };
  checks: EventCheck[];
  summary: {
    callUuid: string | null;
    reportUuid: string | null;
    direction: string | null;
    startedAt: string | null;
    durationSec: number | null;
    agentName: string | null;
    callScore: number | null;
    agentScore: number | null;
    sentiment: string | null;
    outcomeLabel: string | null;
    classifierName: string | null;
    title: string | null;
    partial: boolean;
    signals: { ruleName: string; severity: string }[];
  } | null;
  expiresAt: string;
  body?: unknown;
}

export interface EventStats {
  from: string;
  total: number;
  byStatus: Record<RawEventStatus, number>;
  duplicates: number;
  rejectedAuth: number;
  reconciled: number;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** GET /api/clients/:id — owned by lane B4; typed minimally here (only what this lane reads). */
export interface ClientSite {
  id: string;
  displayName: string;
  nivisionSiteUuid: string;
  slug?: string | null;
  status?: string;
}

export interface ClientLite {
  id: string;
  name: string;
  nivisionTenantSlug?: string | null;
  sites: ClientSite[];
}

/** D-122 "השלמת נתונים": one late batch of the site (GET /api/nivision/sites/:siteId/retro) */
export interface RetroRunView {
  id: string;
  trigger: 'auto_burst' | 'auto_after_silence' | 'admin';
  status: 'open' | 'closed';
  openedAt: string;
  /** admin-held: the run may close only from this time */
  adminUntil: string | null;
  lastLateAt: string | null;
  lateCalls: number;
  /** first / last start time of the late calls */
  from: string | null;
  to: string | null;
  closedAt: string | null;
  /** the "השלמת נתונים" message went out for this run */
  reported: boolean;
}

export interface RetroView {
  open: RetroRunView | null;
  /** last 5 closed runs, newest first */
  recent: RetroRunView[];
}

/** D-147: GET/PUT /api/nivision/ingress-ips — the NIVISION webhook sender allowlist and the addresses seen in 7 days */
export interface IngressIpsView {
  allow: string[];
  seen: { ip: string | null; events: number; quarantined: number; lastAt: string }[];
}
